// The entitlement-API client for the account app.
//
// All of this app's logic lives here rather than in the view: every rule the
// server enforces has a matching error the user must be shown, and those are
// worth testing without a DOM. The view is a thin renderer over these calls.
//
// The browser account app keeps the long-lived refresh token in an HttpOnly API
// cookie and holds the short-lived access token in memory. `TOKEN_KEY` remains as
// a one-read legacy bridge for older localStorage sessions; new sessions do not
// persist bearer tokens in JavaScript-readable storage.

import type { AccountFirebaseAuth, FirebaseAuthResult, TotpEnrollment } from './firebaseAuth';

export type { FirebaseAuthResult, TotpEnrollment } from './firebaseAuth';

export const TOKEN_KEY = 'apicircle-account.token';
export const CSRF_KEY = 'apicircle-account.csrf';
export const CSRF_COOKIE = '__Host-apicircle_account_csrf';
export const CSRF_HEADER = 'x-apicircle-csrf';
export const DEFAULT_API_URL = 'https://api.apicircle.dev';

export interface AccountSummary {
  accountId: string;
  login: string;
  plan: string;
  /** Paid-through, in ms. `null` on a plan with no term. */
  validThrough: number | null;
  subscription: {
    status: string;
    billingPeriod: 'monthly' | 'annual';
    seats: number;
    seatsInUse: number;
    canDecreaseSeats: boolean;
  } | null;
}

export interface Device {
  seatId: string;
  accountId: string;
  deviceId: string | null;
  deviceName: string | null;
}

/** Someone holding a seat in the pool. A seat IS the membership record. */
export interface Member {
  accountId: string;
  email: string | null;
  /** The machine they activated, or `null` while the seat is still waiting. */
  deviceName: string | null;
  activated: boolean;
  isOwner: boolean;
}

export interface CliKeySummary {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

export interface SeatPreview {
  immediateTotal: string;
  nextBillingTotal: string;
  currency: string;
  from: number;
  to: number;
}

export interface AccountApiConfig {
  apiBaseUrl?: string;
  fetchImpl?: typeof fetch;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  firebaseAuth?: AccountFirebaseAuth | null;
  /** Navigates away (Paddle checkout / portal). Default: same-tab. */
  redirect?: (url: string) => void;
  cookieString?: () => string;
}

/**
 * A failed API call, carrying the machine-readable code the server sent.
 *
 * The code matters: `seat_decrease_locked` and `no_seat_available` are policies
 * the UI has to explain, not faults it should render as "something went wrong".
 */
export class AccountApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'AccountApiError';
  }
}

/** Human copy for the error codes the account surfaces can actually produce. */
export function describeApiError(err: AccountApiError): string {
  switch (err.code) {
    case 'seat_decrease_locked':
      return 'Seats can be reduced once your next renewal has been paid at the current count.';
    case 'not_seat_priced':
      return 'Only the Team plan is priced per seat.';
    case 'no_subscription':
    case 'no_licence':
      return 'No active subscription for this account.';
    case 'no_seat_available':
      return 'Every seat is taken. Remove a member, or add seats first.';
    case 'not_pool_owner':
      return 'Only the person who owns the subscription can change who has a seat.';
    case 'already_the_owner':
      return 'That is your own address — your seat is already part of the plan.';
    case 'cannot_remove_owner':
      return 'You cannot remove your own seat. Change the seat count instead.';
    case 'not_a_member':
      return 'They no longer hold a seat. The list has been refreshed.';
    case 'cli_not_included':
      return 'CLI keys are part of the Team plan.';
    case 'too_many_keys':
      return `You have reached the limit of ${String(err.detail?.limit ?? 'your')} CLI keys. Revoke one first.`;
    case 'billing_not_configured':
      return 'Billing is not configured for this deployment.';
    case 'unauthorized':
      return 'Your session has ended. Sign in again.';
    case 'csrf_required':
      return 'Your secure browser session could not be confirmed. Refresh the page and try again.';
    case 'step_up_required':
      return 'Sign in again before making this account change.';
    case 'auth_not_configured':
      return 'Firebase login is not configured for this deployment.';
    case 'mfa_required':
      return 'Enter the code from your authenticator app to continue.';
    case 'email_unverified':
      return 'Verify your email address before continuing.';
    case 'inactive_user':
      return 'This login is disabled. Contact support.';
    case 'sso_not_configured':
      return 'SSO is not configured for that email domain.';
    case 'email_taken':
      return 'That email address is already used by another account.';
    case 'identity_not_found':
      return 'Sign in again before changing that email address.';
    case 'no_change':
      return 'That is already your seat count. Change the number to price a different one.';
    case 'not_found':
      return 'That item no longer exists. The list has been refreshed.';
    default:
      // `call` sets the message to `body.reason` when the server sent one, and
      // falls back to the code itself when it did not. So `message === code` is
      // not a heuristic — it is precisely the case where there is no human
      // sentence to show, and rendering the bare code reads as an internal fault.
      return err.message === err.code ?
          `Something went wrong. Please try again — if it keeps happening, quote code ${err.code}.`
        : err.message;
  }
}

export interface AccountApi {
  token(): string | null;
  setToken(token: string): void;
  establishSession(token: string, refreshToken: string): Promise<void>;
  restoreSession(): Promise<boolean>;
  signOut(): Promise<void>;
  firebaseConfigured(): boolean;
  requestSignInEmail(email: string): Promise<void>;
  completeFirebaseSignIn(url: string, email?: string): Promise<FirebaseAuthResult>;
  beginTotpEnrollment(): Promise<TotpEnrollment>;
  finishTotpEnrollment(code: string): Promise<FirebaseAuthResult>;
  finishTotpSignIn(code: string): Promise<FirebaseAuthResult>;
  establishFirebaseSession(idToken: string, audience?: string): Promise<void>;
  lookupSsoProvider(email: string): Promise<{ providerId: string }>;
  requestSsoSignIn(email: string): Promise<void>;
  requestEmailUpdate(email: string): Promise<void>;
  syncFirebaseSession(): Promise<boolean>;
  account(): Promise<AccountSummary>;
  devices(): Promise<Device[]>;
  deactivateDevice(seatId: string): Promise<void>;
  activationCode(): Promise<{ code: string; expiresIn: number }>;
  previewSeats(seats: number): Promise<SeatPreview>;
  setSeats(seats: number): Promise<{ from: number; to: number; appliedNow: boolean }>;
  members(): Promise<{ members: Member[]; seats: number }>;
  addMember(email: string): Promise<Member>;
  removeMember(accountId: string): Promise<void>;
  cliKeys(): Promise<CliKeySummary[]>;
  createCliKey(name: string): Promise<{ key: string; id: string }>;
  revokeCliKey(id: string): Promise<void>;
  checkout(plan: string, period: 'monthly' | 'annual', seats?: number): Promise<void>;
  openBillingPortal(): Promise<void>;
}

export function createAccountApi(cfg: AccountApiConfig = {}): AccountApi {
  const base = (cfg.apiBaseUrl ?? DEFAULT_API_URL).replace(/\/+$/, '');
  const doFetch = cfg.fetchImpl ?? fetch;
  const storage = cfg.storage ?? window.localStorage;
  const firebaseAuth = cfg.firebaseAuth ?? null;
  const cookieString = cfg.cookieString ?? (() => document.cookie);
  /* v8 ignore start -- jsdom does not implement navigation, so the real default
     cannot be driven from a test; every call site injects one. */
  const redirect = cfg.redirect ?? ((url: string) => window.location.assign(url));
  /* v8 ignore stop */

  let accessToken = storage.getItem(TOKEN_KEY);
  let csrfToken = storage.getItem(CSRF_KEY) ?? readCookie(CSRF_COOKIE, cookieString());

  const token = () => accessToken;

  function setTokenValue(value: string | null): void {
    accessToken = value;
    // Remove a legacy localStorage bearer as soon as this client touches auth.
    storage.removeItem(TOKEN_KEY);
  }

  function setCsrfToken(value: string | null): void {
    csrfToken = value;
    if (value) storage.setItem(CSRF_KEY, value);
    else storage.removeItem(CSRF_KEY);
  }

  function csrfHeaders(): Record<string, string> {
    csrfToken = csrfToken ?? readCookie(CSRF_COOKIE, cookieString());
    return csrfToken ? { [CSRF_HEADER]: csrfToken } : {};
  }

  /** Every AUTHENTICATED call. The one unauthenticated request (the sign-in
   *  email) posts directly, so there is no anonymous branch to get wrong. */
  async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    const bearer = token();
    if (!bearer) throw new AccountApiError('unauthorized', 'Not signed in.', 401);
    const res = await doFetch(`${base}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...csrfHeaders(),
        authorization: `Bearer ${bearer}`,
        ...(init.headers as Record<string, string> | undefined),
      },
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      const code = typeof body.error === 'string' ? body.error : `http_${res.status}`;
      const reason = typeof body.reason === 'string' ? body.reason : code;
      throw new AccountApiError(code, reason, res.status, body);
    }
    return body as T;
  }

  async function establishFirebaseSession(idToken: string, audience?: string): Promise<void> {
    const res = await doFetch(`${base}/auth/firebase/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ idToken, ...(audience ? { audience } : {}) }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      const code = typeof body.error === 'string' ? body.error : `http_${res.status}`;
      throw new AccountApiError(code, code, res.status, body);
    }
    if (typeof body.token !== 'string') throw new AccountApiError('login_failed', 'No session token returned.', 502);
    setCsrfToken(typeof body.csrfToken === 'string' ? body.csrfToken : readCookie(CSRF_COOKIE, cookieString()));
    setTokenValue(body.token);
  }

  async function lookupSsoProvider(email: string): Promise<{ providerId: string }> {
    const res = await doFetch(`${base}/auth/firebase/sso-provider?email=${encodeURIComponent(email.trim())}`);
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || typeof body.providerId !== 'string') {
      const code = typeof body.error === 'string' ? body.error : `http_${res.status}`;
      throw new AccountApiError(code, code, res.status, body);
    }
    return { providerId: body.providerId };
  }

  return {
    token,
    setToken: (t) => setTokenValue(t.trim()),

    async establishSession(rawToken, rawRefreshToken) {
      const submittedToken = rawToken.trim();
      const submittedRefreshToken = rawRefreshToken.trim();
      if (!submittedToken || !submittedRefreshToken) {
        throw new AccountApiError('login_failed', 'Both tokens are required.', 400);
      }
      const res = await doFetch(`${base}/account/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token: submittedToken, refreshToken: submittedRefreshToken }),
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        const code = typeof body.error === 'string' ? body.error : `http_${res.status}`;
        throw new AccountApiError(code, code, res.status, body);
      }
      const issuedToken = typeof body.token === 'string' ? body.token : submittedToken;
      setCsrfToken(typeof body.csrfToken === 'string' ? body.csrfToken : readCookie(CSRF_COOKIE, cookieString()));
      setTokenValue(issuedToken);
    },

    async restoreSession() {
      const res = await doFetch(`${base}/account/session/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...csrfHeaders() },
      });
      if (!res.ok) {
        setTokenValue(null);
        setCsrfToken(null);
        return false;
      }
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (typeof body.token !== 'string') {
        setTokenValue(null);
        setCsrfToken(null);
        return false;
      }
      setCsrfToken(typeof body.csrfToken === 'string' ? body.csrfToken : readCookie(CSRF_COOKIE, cookieString()));
      setTokenValue(body.token);
      return true;
    },

    async signOut() {
      const headers = csrfHeaders();
      setTokenValue(null);
      setCsrfToken(null);
      if (Object.keys(headers).length !== 0) {
        await doFetch(`${base}/account/session/revoke`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', ...headers },
        }).catch(() => undefined);
      }
      await firebaseAuth?.signOut().catch(() => undefined);
    },

    firebaseConfigured: () => firebaseAuth !== null,

    async requestSignInEmail(email) {
      if (!firebaseAuth) throw new AccountApiError('auth_not_configured', 'Firebase login is not configured.', 503);
      await firebaseAuth.sendEmailLink(email);
    },

    async completeFirebaseSignIn(url, emailOverride) {
      if (!firebaseAuth) return { status: 'idle' };
      const email = await firebaseAuth.completeEmailLink(url, emailOverride);
      if (email.status !== 'idle') return email;
      return firebaseAuth.completeRedirect();
    },

    async beginTotpEnrollment() {
      if (!firebaseAuth) throw new AccountApiError('auth_not_configured', 'Firebase login is not configured.', 503);
      return firebaseAuth.beginTotpEnrollment();
    },

    async finishTotpEnrollment(code) {
      if (!firebaseAuth) throw new AccountApiError('auth_not_configured', 'Firebase login is not configured.', 503);
      return firebaseAuth.finishTotpEnrollment(code);
    },

    async finishTotpSignIn(code) {
      if (!firebaseAuth) throw new AccountApiError('auth_not_configured', 'Firebase login is not configured.', 503);
      return firebaseAuth.finishTotpSignIn(code);
    },

    async establishFirebaseSession(idToken, audience) {
      await establishFirebaseSession(idToken, audience);
    },

    async lookupSsoProvider(email) {
      return lookupSsoProvider(email);
    },

    async requestSsoSignIn(email) {
      if (!firebaseAuth) throw new AccountApiError('auth_not_configured', 'Firebase login is not configured.', 503);
      const { providerId } = await lookupSsoProvider(email);
      await firebaseAuth.startSso(providerId);
    },

    async requestEmailUpdate(email) {
      if (!firebaseAuth) throw new AccountApiError('auth_not_configured', 'Firebase login is not configured.', 503);
      await firebaseAuth.requestEmailUpdate(email);
    },

    async syncFirebaseSession() {
      if (!firebaseAuth) return false;
      const idToken = await firebaseAuth.currentIdToken();
      if (!idToken) return false;
      await establishFirebaseSession(idToken);
      return true;
    },

    account: () => call<AccountSummary>('/account'),
    devices: () => call<{ devices: Device[] }>('/account/devices').then((r) => r.devices),

    deactivateDevice: (seatId) =>
      call<void>('/account/devices/deactivate', {
        method: 'POST',
        body: JSON.stringify({ seatId }),
      }).then(() => undefined),

    activationCode: () =>
      call<{ code: string; expiresIn: number }>('/activation/code', { method: 'POST' }),

    previewSeats: (seats) =>
      call<SeatPreview>('/account/seats/preview', { method: 'POST', body: JSON.stringify({ seats }) }),

    setSeats: (seats) =>
      call<{ from: number; to: number; appliedNow: boolean }>('/account/seats', {
        method: 'POST',
        body: JSON.stringify({ seats }),
      }),

    members: () => call<{ members: Member[]; seats: number }>('/account/members'),

    addMember: (email) =>
      call<{ member: Member }>('/account/members', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }).then((r) => r.member),

    removeMember: (accountId) =>
      call<void>(`/account/members/${encodeURIComponent(accountId)}`, { method: 'DELETE' }).then(
        () => undefined,
      ),

    cliKeys: () => call<{ keys: CliKeySummary[] }>('/account/cli-keys').then((r) => r.keys),

    createCliKey: (name) =>
      call<{ key: string; id: string }>('/account/cli-keys', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),

    revokeCliKey: (id) =>
      call<void>('/account/cli-keys/revoke', { method: 'POST', body: JSON.stringify({ id }) }).then(
        () => undefined,
      ),

    async checkout(plan, period, seats) {
      const { url } = await call<{ url: string }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan, period, ...(seats === undefined ? {} : { quantity: seats }) }),
      });
      redirect(url);
    },

    async openBillingPortal() {
      const { url } = await call<{ url: string }>('/billing/portal');
      redirect(url);
    },
  };
}

function readCookie(name: string, cookieHeader: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const [rawName, ...rawValue] = part.trim().split('=');
    if (rawName === name) return decodeURIComponent(rawValue.join('='));
  }
  return null;
}
